import { Injectable } from '@angular/core';
import { ReplaySubject } from 'rxjs';

/**
 * Manages the ready state of cookie device ID (cdid) initialization.
 *
 * This service tracks whether the userService.fetch() call has completed
 * (successfully or with error) or timed out. Other HTTP requests should wait
 * for this service to be ready before proceeding, to ensure the cdid is
 * properly set by the backend.
 */
@Injectable({ providedIn: 'root' })
export class CookieDeviceIdReadyService {
  private isReady = false;
  private isTimeout = false;
  private ready$ = new ReplaySubject<void>(1);
  private timeoutMs = 10000; // 10 seconds timeout

  constructor() {
    // Start timeout timer
    setTimeout(() => {
      if (!this.isReady) {
        this.isTimeout = true;
        // Release all waiting requests even if not ready (degraded mode)
        this.ready$.next();
        this.ready$.complete();
      }
    }, this.timeoutMs);
  }

  /**
   * Mark the cookie device ID as ready.
   * Should be called after userService.fetch() completes (success or error).
   */
  markAsReady(): void {
    if (!this.isReady) {
      this.isReady = true;
      this.ready$.next();
      this.ready$.complete();
    }
  }

  /**
   * Wait for the cookie device ID to be ready or timeout.
   * Returns immediately if already ready or timed out.
   */
  async waitForReadyOrTimeout(): Promise<void> {
    if (this.isReady || this.isTimeout) {
      return;
    }
    return this.ready$.toPromise();
  }

  /**
   * Check if the service is currently ready.
   */
  isReadyNow(): boolean {
    return this.isReady;
  }

  /**
   * Check if the timeout has been reached.
   */
  isTimeoutReached(): boolean {
    return this.isTimeout;
  }
}
